import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setShowMobileMenu(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const navbarHeight = 64;
      const top = el.getBoundingClientRect().top + window.scrollY - navbarHeight - 8;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 50);
  };

  const navLinks = [
    { label: 'Avantages', id: 'avantages', icon: 'ri-star-line' },
    { label: 'Réalisations', id: 'portfolio', icon: 'ri-gallery-line' },
    { label: 'Tarifs', id: 'tarifs', icon: 'ri-price-tag-3-line' },
    { label: 'FAQ', id: 'faq', icon: 'ri-question-line' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/97 backdrop-blur-xl shadow-sm border-b border-slate-100/80' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2.5 sm:space-x-3 flex-shrink-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl overflow-hidden shadow-md flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/a17d3ae5e640a18a289681692364ae47/fc2ed0bd71fb688a40f03f8096cc646d.jpeg"
                alt="UzunDigital"
                className="w-full h-full object-cover"
                loading="eager"
                width="40"
                height="40"
              />
            </div>
            <span className={`text-lg sm:text-xl font-black transition-colors ${scrolled ? 'text-slate-900' : 'text-white'}`}>
              UzunDigital
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  scrolled
                    ? 'text-slate-600 hover:text-emerald-600 hover:bg-emerald-50'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {item.label}
              </button>
            ))}
            <Link
              to="/offres"
              className={`text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'text-slate-600 hover:text-emerald-600 hover:bg-emerald-50'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              Nos Offres
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+33651872435"
              className={`flex items-center gap-1.5 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-slate-600 hover:text-emerald-600' : 'text-white/80 hover:text-white'}`}
            >
              <i className="ri-phone-line text-base"></i>
              <span>06 51 87 24 35</span>
            </a>
            <Link
              to="/demande-devis"
              className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-5 py-2.5 rounded-full text-sm font-bold hover:shadow-lg hover:shadow-emerald-500/30 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Devis Gratuit
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="Menu"
          >
            <i className={`${showMobileMenu ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${scrolled ? 'text-slate-900' : 'text-white'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ${showMobileMenu ? 'max-h-screen' : 'max-h-0'}`}>
        <div className="bg-white border-t border-slate-100 shadow-xl">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer w-full text-left"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${item.icon} text-emerald-500 text-lg`}></i>
                </div>
                <span className="text-slate-800 font-semibold text-sm">{item.label}</span>
              </button>
            ))}
            <Link
              to="/offres"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-store-2-line text-emerald-500 text-lg"></i>
              </div>
              <span className="text-slate-800 font-semibold text-sm">Nos Offres</span>
            </Link>

            <div className="pt-2 grid grid-cols-2 gap-2">
              <a
                href="tel:+33651872435"
                className="flex items-center justify-center gap-2 border-2 border-slate-200 text-slate-700 px-4 py-3 rounded-xl font-semibold text-sm hover:border-emerald-400 transition-colors cursor-pointer"
              >
                <i className="ri-phone-line text-emerald-500"></i>
                <span>Appeler</span>
              </a>
              <Link
                to="/demande-devis"
                onClick={() => setShowMobileMenu(false)}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-3 rounded-xl font-bold text-sm cursor-pointer"
              >
                <i className="ri-file-text-line"></i>
                <span>Devis Gratuit</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
